import React, { useEffect, useMemo, useState } from "react";
import { muAxios } from "../../api/axios";
import { useAuthContext } from "../../hooks/useAuthContext";
import { Link, useParams } from "react-router-dom";
import RightReserve from "./RightsReserve";
import { useToast } from "../../context/ToastContext";
import { PageSkeleton } from "../../components/Skeleton";

function SingleCourse() {
  const { user } = useAuthContext();
  const { notifyError } = useToast();
  const { course } = useParams();

  const [logo, setLogo] = useState("");
  const [title, setTitle] = useState("");

  const [chapters, setChapters] = useState([]); // mu_chapter rows
  const [chapTitle, setChapTitle] = useState([]); // chapter titles array

  const [progress, setProgress] = useState(0); // course-level currentLesson
  const [scriptReady, setScriptReady] = useState(false);

  // For each chapter: { [chapterNo]: { firstLesson, totalLessons } }
  const [chapterMetaMap, setChapterMetaMap] = useState({});

  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [pageNotFound, setPageNotFound] = useState(false);

  const headers = useMemo(
    () => ({ headers: { accessToken: user?.token } }),
    [user?.token]
  );

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      if (!user?.token || !course) return;

      setLoading(true);
      setPageError("");
      setPageNotFound(false);

      try {
        const [pRes, courseRes, chapRes, scriptRes] = await Promise.all([
          muAxios.get(`/api/mu/get/${course}/progress`, headers),
          muAxios.get(`/api/mu/get/course/${course}`, headers),
          muAxios.get(`/api/mu/get/chapter/${course}`, headers),
          muAxios.get(`/api/mu/check/script/${course}`, headers),
        ]);

        if (!mounted) return;

        // progress
        setProgress(Number(pRes?.data?.progress || 0));

        // course info
        const cjson = courseRes?.data;
        const hasCoursePayload =
          !!cjson && typeof cjson === "object" && Object.keys(cjson).length > 0;

        if (!hasCoursePayload) {
          setPageNotFound(true);
          setLogo("");
          setTitle("");
          setChapters([]);
          setChapTitle([]);
          setScriptReady(false);
          return;
        }

        if (cjson) {
          setLogo(process.env.REACT_APP_MU + cjson.logo);
          setTitle(cjson.title || "");
        }

        // chapters
        const chjson = chapRes?.data;
        const list = chjson?.chapter || [];
        setChapters(Array.isArray(list) ? list : []);
        setChapTitle(Array.isArray(chjson?.chapterTitle) ? chjson.chapterTitle : []);

        // script ready
        setScriptReady(!!scriptRes?.data?.progress);
      } catch (e) {
        if (!mounted) return;
        setPageError("Unable to load course. Please refresh and try again.");
        notifyError("Failed to load course.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    run();
    return () => (mounted = false);
  }, [user?.token, course, headers, notifyError]);

  // Build chapterMetaMap by fetching lesson list per chapter (to find firstLesson global order)
  useEffect(() => {
    let mounted = true;

    const buildMeta = async () => {
      if (!user?.token || !course || !chapters?.length) return;

      try {
        const results = await Promise.all(
          chapters.map(async (ch) => {
            const chNo = Number(ch.chapter);
            const res = await muAxios.get(`/api/mu/get/lesson/all/${course}/${chNo}`, headers);
            const lessons = Array.isArray(res?.data) ? res.data : [];
            const lessonNumbers = lessons.map((x) => Number(x.lesson)).filter((n) => Number.isFinite(n));

            const firstLesson = lessonNumbers.length ? Math.min(...lessonNumbers) : null;
            const totalLessons = lessonNumbers.length ? lessonNumbers.length : 0;

            return [chNo, { firstLesson, totalLessons }];
          })
        );

        if (!mounted) return;

        const map = {};
        results.forEach(([chNo, meta]) => {
          map[chNo] = meta;
        });
        setChapterMetaMap(map);
      } catch (e) {
        // Meta fail shouldn't block page; chapters still visible without accurate locks
      }
    };

    buildMeta();
    return () => (mounted = false);
  }, [user?.token, course, chapters, headers]);

  const courseLabel = title || course;

  const computeChapterProgress = (chNo) => {
    const p = Number(progress || 0);
    const meta = chapterMetaMap?.[chNo];

    // fallback: old logic, best-effort
    if (!meta || !meta.firstLesson || !meta.totalLessons) {
      return { unlocked: true, completed: 0, total: 0, percent: 0 };
    }

    const first = Number(meta.firstLesson);
    const total = Number(meta.totalLessons);

    // if progress hasn't reached this chapter start => locked
    if (p < first) return { unlocked: false, completed: 0, total, percent: 0 };

    // completed within chapter = progress - first (cap to total)
    const completed = Math.max(0, Math.min(total, p - first));
    const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

    return { unlocked: true, completed, total, percent };
  };

  if (loading) return <PageSkeleton variant="grid" cards={6} />;

  if (!loading && pageNotFound) {
    return (
      <div>
        <RightReserve />

        <div className="container py-4">
          <nav aria-label="breadcrumb" className="mb-3">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <Link to="/courses">Courses</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                {course}
              </li>
            </ol>
          </nav>

          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body py-5 text-center">
              <div className="fw-semibold mb-2" style={{ fontSize: 22 }}>
                Page not found
              </div>
              <div className="text-muted mx-auto" style={{ fontSize: 14, maxWidth: 520 }}>
                Course {course} does not exist.
              </div>

              <div className="d-flex justify-content-center gap-2 mt-4 flex-wrap">
                <Link className="btn btn-dark" to="/courses">
                  Back to Courses
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <RightReserve />

      <div className="container py-4">
        {/* Breadcrumb */}
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <Link to="/courses">Courses</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              {courseLabel}
            </li>
          </ol>
        </nav>

        {/* Header */}
        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 mb-4">
          <div className="d-flex align-items-center gap-3">
            {/* Logo ratio-safe */}
            <div
              className="rounded-4 border bg-white d-flex align-items-center justify-content-center"
              style={{ width: 110, height: 80, padding: 10 }}
            >
              {logo ? (
                <img
                  src={logo}
                  alt={courseLabel}
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                />
              ) : (
                <span className="text-muted" style={{ fontSize: 12 }}>
                  No logo
                </span>
              )}
            </div>

            <div>
              <div className="fw-semibold" style={{ fontSize: 20 }}>
                {courseLabel}
              </div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                Browse chapters and continue your lessons.
              </div>
            </div>
          </div>

          <div className="text-muted" style={{ fontSize: 13 }}>
            {loading ? "Loading..." : `Current lesson: ${progress || 0}`}
          </div>
        </div>

        {/* Error */}
        {pageError && (
          <div className="alert alert-danger" role="alert">
            {pageError}
          </div>
        )}

        {/* Script ready banner */}
        {scriptReady && (
          <div className="alert alert-success d-flex align-items-center justify-content-between gap-3" role="alert">
            <div>
              <div className="fw-semibold">Course completed</div>
              <div style={{ fontSize: 13 }}>
                You can now view your custom scripts for this course.
              </div>
            </div>
            <Link to={`/courses/${course}/script`} className="btn btn-sm btn-success">
              View My Scripts
            </Link>
          </div>
        )}

        {/* Empty */}
        {!loading && !pageError && chapters.length === 0 && (
          <div className="text-center text-muted py-5">No chapters found.</div>
        )}

        {/* Chapters grid */}
        <div className="row g-3">
          {chapters.map((ch, i) => {
            const chNo = Number(ch.chapter);
            const toChapter = `/courses/${course}/${chNo}`;

            const meta = computeChapterProgress(chNo);
            const chapName = chapTitle?.[i] || `Chapter ${chNo}`;

            return (
              <div key={chNo} className="col-12 col-md-6 col-lg-4">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-start justify-content-between gap-2">
                      <div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          CHAPTER {chNo}
                        </div>
                        <div className="fw-semibold mt-1" style={{ fontSize: 16 }}>
                          {chapName}
                        </div>
                      </div>

                      {meta.unlocked ? (
                        <span className="badge rounded-pill bg-success">Unlocked</span>
                        ) : (
                        <span className="badge rounded-pill bg-light text-muted border">Preview</span>
                        )}
                    </div>

                    <div className="mt-3">
                      <div className="d-flex justify-content-between text-muted" style={{ fontSize: 12 }}>
                        <span>Chapter progress</span>
                        <span>
                          {meta.completed}/{meta.total} • {meta.percent}%
                        </span>
                      </div>
                      <div className="progress" style={{ height: 8 }}>
                        <div className="progress-bar" role="progressbar" style={{ width: `${meta.percent}%` }} />
                      </div>
                    </div>

                    <div className="d-grid gap-2 mt-4">
                        <Link
                            className={`btn ${meta.unlocked ? "btn-dark" : "btn-outline-dark"}`}
                            to={toChapter}
                            >
                            View Chapter
                        </Link>
                    </div>

                    {!meta.unlocked && (
                    <div className="text-muted mt-3" style={{ fontSize: 12 }}>
                        Preview available. Complete earlier lessons to unlock and start learning.
                    </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer small */}
        <div className="text-muted mt-4" style={{ fontSize: 12 }}>
          Tip: Your progress is tracked by lesson order across the whole course.
        </div>
      </div>
    </div>
  );
}

export default SingleCourse;
